import { create } from "zustand";
import { supabase } from 'src/lib/supabaseClient';


// Define the profile data interface to match your database schema
export interface ProfileData {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string; // Email cannot be null
  userType: string | null;
  profilePictureUrl: string | null;
  timezone: string | null; // User's timezone in IANA format
  createdAt: Date | null;
  updatedAt: Date | null;
}

// Extended ProfileData for students
export interface StudentProfileData extends ProfileData {
  // Onboarding fields
  educationLevel?: string;
  subjectsOfInterest?: string[];
  learningGoals?: string[];
  studyPreferences?: {
    preferredTime: string;
    preferredDays: string[];
    learningStyle: string;
    communicationPreference: string;
  };
  academicHistory?: {
    schoolName: string;
    achievements: string[];
    favoriteSubjects: string[];
  };

  // Additional fields
  hobbies?: string[];
  interests?: string[];
  location?: string;
}

// Extended ProfileData for tutors
export interface TutorProfileData extends ProfileData {
  // Tutor-specific fields
  educationLevel?: string;
  hourlyRate?: number;
  subjectsTaught?: string[] | string; // Can be array or string for backward compatibility
  teachingExperience?: string;
  bio?: string;
  verificationStatus?: string;
  cvFilePath?: string;
  rating?: number;
  dateOfBirth?: string | null;

  // New enhanced tutor fields
  achievements?: string[];
  expertise?: string[];
  education?: any[]; // Education records with degree, branch, university, year

  // Related data
  availabilitySlots?: any[]; // Availability slots
  specializations?: string[];
  subjects?: string[]; // Parsed from subjectsTaught
}

// Combined ProfileData that can handle both student and tutor data
export interface CombinedProfileData extends ProfileData {
  educationLevel: string;
  subjectsOfInterest: string[];
  learningGoals: string[];
  studyPreferences: {
    preferredTime: string;
    preferredDays: string[];
    learningStyle: string;
    communicationPreference: string;
  };
  academicHistory: {
    schoolName: string;
    grade?: string;
    achievements: string[];
    favoriteSubjects: string[];
  };
  hobbies: string[];
  interests: string[];
  location: string;
  // Tutor-specific fields (optional)
  educationLevelTutor?: string;
  hourlyRate?: number;
  subjectsTaught?: string[] | string;
  teachingExperience?: string;
  bio?: string;
  verificationStatus?: string;
  cvFilePath?: string;
  rating?: number;
  dateOfBirth?: string | null;
  achievements?: string[];
  expertise?: string[];
  education?: any[];
  availabilitySlots?: any[];
  specializations?: string[];
  subjects?: string[];
}

// Define the profile store interface
interface ProfileStore {
  // State
  profileData: CombinedProfileData; // Using CombinedProfileData to include all possible fields
  isLoading: boolean;
  error: string | null;

  // Actions
  setProfileData: (data: Partial<CombinedProfileData>) => void;
  updateProfile: (userId: string, onboardingCompleted?: boolean) => Promise<void>;
  fetchStudentData: (userId: string) => Promise<any>;
  updateStudentProfile: (userId: string) => Promise<void>;
  resetProfileData: () => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// Create the store
export const useProfileStore = create<ProfileStore>((set, get) => ({
  // Initial state
  profileData: {
    id: "",
    firstName: null,
    lastName: null,
    email: "", // Initialize with empty string instead of null
    userType: null,
    profilePictureUrl: null,
    timezone: null,
    createdAt: null,
    updatedAt: null,
    educationLevel: "",
    subjectsOfInterest: [],
    learningGoals: [],
    studyPreferences: {
      preferredTime: "",
      preferredDays: [],
      learningStyle: "",
      communicationPreference: ""
    },
    academicHistory: {
      schoolName: "",
      grade: "",
      achievements: [],
      favoriteSubjects: []
    },
    hobbies: [],
    interests: [],
    location: ""
  },
  isLoading: false,
  error: null,

  // Actions
  setProfileData: (data) => set((state) => ({
    profileData: { ...state.profileData, ...data }
  })),

  updateProfile: async (userId, onboardingCompleted = true) => {
    set({ isLoading: true, error: null });

    // Only fetch profile if onboarding is completed
    if (!onboardingCompleted) {
      set({ isLoading: false });
      return;
    }

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("first_name, last_name, email, user_type, profile_picture_url, created_at, updated_at")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        set({ error: error.message, isLoading: false });
        return;
      }

      if (data) {
        set({
          profileData: {
            ...get().profileData, // Keep existing student data
            id: userId,
            firstName: data.first_name || null,
            lastName: data.last_name || null,
            email: data.email, // Email is required, no fallback needed
            userType: data.user_type || null,
            profilePictureUrl: data.profile_picture_url || null,
            createdAt: data.created_at ? new Date(data.created_at) : null,
            updatedAt: data.updated_at ? new Date(data.updated_at) : null
            // Student-specific fields are set in updateStudentProfile
          },
          isLoading: false
        });
        get().setIsLoading(false);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      set({
        error: error instanceof Error ? error.message : "Unknown error occurred",
        isLoading: false
      });
    }
  },

  // New method to fetch student-specific data
  fetchStudentData: async (userId) => {

    try {
      // First check if the student record exists without using .single()
      const { data: checkData, error: checkError } = await supabase
        .from("students")
        .select("id")
        .eq("id", userId);

      // If there's an error checking or no records found
      if (checkError || !checkData || checkData.length === 0) {
        console.log("No student record found during pre-check:", checkError || "No records");
        // Return null instead of throwing an error - this is a valid state
        return null;
      }

      // If we get here, we know the record exists, so we can use .maybeSingle() instead of .single()
      const { data, error } = await supabase
        .from("students")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.log("Error fetching student data after confirming existence:", error);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Unexpected error in fetchStudentData:", error);
      // Don't throw, just return null and let the caller handle it
      return null;
    }
  },

  // New method to update student profile data
  updateStudentProfile: async (userId) => {
    set({ isLoading: true, error: null });

    try {
      // First get the basic profile data
      await get().updateProfile(userId);

      // Then fetch student-specific data
      const studentData = await get().fetchStudentData(userId);

      // Empty defaults for student data
      const emptyStudentData = {
        educationLevel: "",
        subjectsOfInterest: [],
        learningGoals: [],
        studyPreferences: {
          preferredTime: "",
          preferredDays: [],
          learningStyle: "",
          communicationPreference: ""
        },
        academicHistory: {
          schoolName: "",
          grade: "",
          achievements: [],
          favoriteSubjects: []
        },
        hobbies: [],
        interests: [],
        location: ""
      };

      if (studentData) {
        // Update the profile data with student-specific fields
        set((state) => ({
          profileData: {
            ...state.profileData,
            educationLevel: studentData.educationLevel || emptyStudentData.educationLevel,
            subjectsOfInterest: studentData.subjectsOfInterest || emptyStudentData.subjectsOfInterest,
            learningGoals: studentData.learningGoals || emptyStudentData.learningGoals,
            studyPreferences: studentData.studyPreferences || emptyStudentData.studyPreferences,
            academicHistory: studentData.academicHistory || emptyStudentData.academicHistory,
            hobbies: studentData.hobbies || emptyStudentData.hobbies,
            interests: studentData.interests || emptyStudentData.interests,
            location: studentData.location || emptyStudentData.location
          },
          isLoading: false,
          error: null
        }));
      } else {
        // If no student data found, use empty defaults and set a user-friendly message
        set((state) => ({
          profileData: {
            ...state.profileData,
            ...emptyStudentData
          },
          isLoading: false,
          error: "No student record found. If issue persists after re-try then contact admin."
        }));
      }
    } catch (error) {
      console.error("Error updating student profile:", error);
      set({
        error: "Error loading student profile data. If issue persists after re-try then contact admin.",
        isLoading: false
      });
      // Don't re-throw the error - we've handled it here
    }
  },

  resetProfileData: () => set({
    profileData: {
      id: "",
      firstName: null,
      lastName: null,
      email: "", // Reset to empty string instead of null
      userType: null,
      profilePictureUrl: null,
      timezone: null,
      createdAt: null,
      updatedAt: null,
      educationLevel: "",
      subjectsOfInterest: [],
      learningGoals: [],
      studyPreferences: {
        preferredTime: "",
        preferredDays: [],
        learningStyle: "",
        communicationPreference: ""
      },
      academicHistory: {
        schoolName: "",
        achievements: [],
        favoriteSubjects: []
      },
      hobbies: [],
      interests: [],
      location: ""
    }
  }),

  setIsLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set({ error })
}));
